import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { tt } from '@/internationalization/i18n'
import { toast } from '@/stores'
import { patronsApi } from './api'

export function usePatrons(includeArchived = false) {
  return useQuery({
    queryKey: qk.patrons.list(includeArchived),
    queryFn: () => patronsApi.list(includeArchived),
    // The filter dropdown reads this on every page; it changes rarely.
    staleTime: 60_000,
  })
}

export function usePatronSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: qk.patrons.suggestions,
    queryFn: () => patronsApi.suggestions(50),
    enabled,
  })
}

/**
 * Every patron refusal is something a person can fix, so each one keeps its own
 * message rather than collapsing into "could not save".
 *
 * `patron_name_taken` gets its own i18n'd body rather than falling through to
 * `error.message` -- that message is the backend's fixed English sentence, so
 * a Hindi-locale user renaming into a clash would otherwise read one line of
 * English in the middle of an otherwise-translated toast.
 */
function explain(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.status < 500) {
    if (error.code === 'patron_name_taken') {
      return toast.error(fallback, tt('patrons.toast.nameTaken'))
    }
    return toast.error(fallback, error.message)
  }
  return toast.error(fallback, tt('common.somethingWentWrong'))
}

function usePatronMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  fallback: string,
  onDone?: (result: TResult) => void,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: qk.patrons.all })
      // Claiming or releasing a domain reclassifies HISTORY, so every meeting
      // and action list on screen is now stale.
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
      queryClient.invalidateQueries({ queryKey: qk.actions.all })
      onDone?.(result)
    },
    onError: (error) => explain(error, fallback),
  })
}

export const useCreatePatron = () =>
  usePatronMutation(patronsApi.create, tt('patrons.toast.addFail'), () =>
    toast.success(tt('patrons.toast.added')),
  )

export const useUpdatePatron = () =>
  usePatronMutation(
    ({ id, patch }: { id: string; patch: Parameters<typeof patronsApi.update>[1] }) =>
      patronsApi.update(id, patch),
    tt('patrons.toast.saveFail'),
  )

export const useAddPatronDomain = () =>
  usePatronMutation(
    ({ id, domain }: { id: string; domain: string }) => patronsApi.addDomain(id, domain),
    tt('patrons.toast.domainAddFail'),
    (patron) =>
      toast.success(
        tt('patrons.toast.domainAdded'),
        tt(
          patron.meetingCount === 1
            ? 'patrons.toast.domainAddedBodyOne'
            : 'patrons.toast.domainAddedBodyMany',
          { name: patron.name, count: patron.meetingCount },
        ),
      ),
  )

export const useRemovePatronDomain = () =>
  usePatronMutation(
    ({ id, domain }: { id: string; domain: string }) => patronsApi.removeDomain(id, domain),
    tt('patrons.toast.domainRemoveFail'),
    () => toast.info(tt('patrons.toast.domainRemoved'), tt('patrons.toast.domainRemovedBody')),
  )

export const useSetMeetingPatrons = () =>
  usePatronMutation(
    ({ meetingId, patronIds }: { meetingId: string; patronIds: string[] }) =>
      patronsApi.setMeetingPatrons(meetingId, patronIds),
    tt('patrons.toast.meetingPatronsFail'),
  )
